import { useLocale } from '../../application/i18n/LocaleProvider';
import { QUICK_FILTERS, countActiveFilters, isFilterActive } from '../../domain/plants/plantFilters';
import { ChevronDownIcon } from '../chrome/BrandIcons';
import { SearchIcon, SlidersIcon } from '../icons/ControlIcons';
import { TRAIT_ICONS } from './PlantIcons';

/**
 * Search, Filters and the quick chips — the block that stays on screen while the
 * shelves scroll under it (SPEC-011 D-7).
 *
 * A quick chip is not a second filter mechanism. Each one is a `(group, value)`
 * pair pointing into the same object the sheet edits, so a chip pressed here is
 * the box already ticked when the sheet opens (D-4).
 */
export default function CatalogueControls({
    query,
    onQueryChange,
    filters,
    onToggleFilter,
    onClearFilters,
    onOpenFilters,
    resultCount,
}) {
    const { t } = useLocale();
    const activeCount = countActiveFilters(filters);

    return (
        <div className="catalogue-controls">
            <div className="catalogue-controls__row">
                <label className="plant-search">
                    <SearchIcon size={20} />
                    <input
                        className="plant-search__input"
                        type="search"
                        value={query}
                        onChange={(event) => onQueryChange(event.target.value)}
                        placeholder={t('plants.search.placeholder')}
                        aria-label={t('plants.search.label')}
                    />
                </label>
                <button
                    type="button"
                    className="filter-button"
                    onClick={onOpenFilters}
                    // The badge is a numeral in a circle. Spelled out here so the
                    // count is not something only a sighted user can read.
                    aria-label={activeCount > 0
                        ? t('plants.filter.openWithCount', { count: activeCount })
                        : t('plants.filter.open')}
                >
                    <SlidersIcon size={20} />
                    <span className="filter-button__label">{t('plants.filter.open')}</span>
                    {activeCount > 0 ? (
                        <span className="filter-button__badge" aria-hidden="true">{activeCount}</span>
                    ) : null}
                </button>
            </div>

            <div className="quick-filters" role="group" aria-label={t('plants.filter.quickLabel')}>
                <button
                    type="button"
                    className={`quick-filter${activeCount === 0 ? ' quick-filter--active' : ''}`}
                    onClick={onClearFilters}
                    aria-pressed={activeCount === 0}
                >
                    {t('plants.filter.all')}
                </button>
                {QUICK_FILTERS.map((chip) => {
                    const active = isFilterActive(filters, chip.groupKey, chip.value);
                    const ChipIcon = TRAIT_ICONS[chip.labelKey];

                    return (
                        <button
                            type="button"
                            key={chip.key}
                            className={`quick-filter${active ? ' quick-filter--active' : ''}`}
                            onClick={() => onToggleFilter(chip.groupKey, chip.value)}
                            aria-pressed={active}
                        >
                            {ChipIcon ? <ChipIcon size={18} /> : null}
                            {t(chip.labelKey)}
                        </button>
                    );
                })}
                <button type="button" className="quick-filter" onClick={onOpenFilters}>
                    {t('plants.filter.more')}
                    <ChevronDownIcon size={18} />
                </button>
            </div>

            {/* The count only appears once the catalogue has been narrowed. On the
                whole shelf it would be a number nobody asked for; on a narrowed
                one it is the answer to "did that do anything?". */}
            {activeCount > 0 || query.trim() ? (
                <p className="catalogue-controls__count" aria-live="polite">
                    {t(resultCount === 1 ? 'plants.results.countOne' : 'plants.results.count', {
                        count: resultCount,
                    })}
                </p>
            ) : null}
        </div>
    );
}
